import { LazyMotion } from "framer-motion";
import { memo, useEffect } from "react";
import type { MouseEvent } from "react";
import { BottomTabBar } from "./BottomTabBar.tsx";
import GlobalLoadingOverlay from "./GlobalLoadingOverlay.tsx";
import { MobilePrimaryCTA } from "./MobilePrimaryCTA.tsx";
import { LeagueTopBar } from "./LeagueTopBar.tsx";
import { MobileTopBar } from "./NavBar.tsx";
import { Notifications } from "./Notifications.tsx";
import { MobileHighlightCard } from "../MobileHighlightCard.tsx";
import { MobileMomentOverlay } from "../MobileMomentOverlay.tsx";
import { TitleBar } from "./TitleBar.tsx";
import { getMobileAppShellPolicy } from "./appShellPolicy.ts";
import { isMobileCoreLoopV4Enabled } from "../../util/mobileCoreLoopFeatureFlag.ts";
import { isMobileIAV3Enabled } from "../../util/mobileIAFeatureFlag.ts";
import { syncMobileBackStack } from "../../util/mobileBackStack.ts";
import { useLocalPartial } from "../../util/local.ts";
import { useViewData } from "../../util/viewManager.tsx";
import { ErrorBoundary } from "../ErrorBoundary.tsx";
import useIsMobileViewport from "../../hooks/useIsMobileViewport.ts";
import { parseMobileReturnState } from "../../util/mobilePageContext.ts";
import { resolveMobileSection } from "../../util/mobileNavigation.ts";
import i18n from "../../util/i18n.ts";
import {
	preserveMobileRouteSheetHref,
	rewriteMobileCrossTabHref,
} from "../../util/mobileCrossTabLink.ts";

const loadFramerMotionFeatures = () =>
	import("../../util/framerMotionFeatures.ts").then((res) => res.default);

const minHeight100 = {
	// Just using h-100 class here results in the sticky ad in the skyscraper becoming unstuck after scrolling down 100% of the viewport, for some reason
	minHeight: "100%",
};

const minWidth0 = {
	// Fix for responsive table not being triggered by flexbox limits, and skyscraper ad overflowing content https://stackoverflow.com/a/36247448/786644
	minWidth: 0,
};

const getMobileRouteSheetTitle = (pageID: unknown) => {
	const titles: Record<string, { key: string; defaultValue: string }> = {
		draft: { key: "draft.title", defaultValue: "Draft" },
		draftHistory: { key: "draftHistory.title", defaultValue: "Draft History" },
		draftLottery: { key: "draftLottery.title", defaultValue: "Draft Lottery" },
		draftScouting: {
			key: "draftScouting.title",
			defaultValue: "Draft Scouting",
		},
		freeAgents: { key: "freeAgents.title", defaultValue: "Free Agents" },
		negotiationList: {
			key: "mobileNav.negotiation.label",
			defaultValue: "Negotiations",
		},
		protectPlayers: {
			key: "protectPlayers.title",
			defaultValue: "Protect Players",
		},
		roster: { key: "roster.title", defaultValue: "Roster" },
		teamFinances: { key: "teamFinances.title", defaultValue: "Team Finances" },
		trade: { key: "mobileNav.trade.label", defaultValue: "Trade" },
	};
	const descriptor = typeof pageID === "string" ? titles[pageID] : undefined;
	return descriptor
		? i18n.t(descriptor.key, {
				ns: "ui",
				defaultValue: descriptor.defaultValue,
			})
		: i18n.t("common.details", { ns: "ui", defaultValue: "Details" });
};

type KeepPreviousRenderWhileUpdatingProps = {
	children: any;
	updating: boolean;
};
const KeepPreviousRenderWhileUpdating = memo(
	(props: KeepPreviousRenderWhileUpdatingProps) => {
		return props.children;
	},
	(
		prevProps: KeepPreviousRenderWhileUpdatingProps,
		nextProps: KeepPreviousRenderWhileUpdatingProps,
	) => {
		// No point in rendering while updating contents
		return nextProps.updating;
	},
);

export const Controller = () => {
	const state = useViewData();

	const {
		Component,
		data,
		idLoaded,
		idLoading,
		inLeague,
		loading: updating,
		scrollToTop,
	} = state;
	const pageID = idLoading ?? idLoaded;
	const { phase, playMenuOptions } = useLocalPartial([
		"phase",
		"playMenuOptions",
	]);
	const backStackUrl =
		typeof window !== "undefined"
			? window.location.pathname + window.location.search
			: "";
	const playerTid =
		pageID === "player" && typeof data?.player?.tid === "number"
			? data.player.tid
			: undefined;
	const isMobile = useIsMobileViewport();
	const mobileReturnState =
		typeof window !== "undefined"
			? parseMobileReturnState(window.location.search)
			: {};
	const targetSection = resolveMobileSection(pageID, { playerTid });
	const routeSheetReturnHref =
		mobileReturnState.returnHref ?? mobileReturnState.returnUrl;
	const showMobileRouteSheet = Boolean(
		isMobile &&
		mobileReturnState.sourcePage &&
		routeSheetReturnHref &&
		(mobileReturnState.presentation === "sheet" ||
			(mobileReturnState.sourceSection &&
				targetSection &&
				mobileReturnState.sourceSection !== targetSection)),
	);
	const routeSheetTitle = getMobileRouteSheetTitle(pageID);
	const renderedView = Component ? (
		<KeepPreviousRenderWhileUpdating updating={updating}>
			<Component {...data} />
		</KeepPreviousRenderWhileUpdating>
	) : null;
	const handleContentClickCapture = (event: MouseEvent<HTMLElement>) => {
		if (!isMobile || !inLeague) {
			return;
		}

		const target = event.target as Element | null;
		const anchor = target?.closest<HTMLAnchorElement>("a[href]");
		if (
			!anchor ||
			anchor.hasAttribute("download") ||
			anchor.hasAttribute("data-mobile-cross-tab-route-close") ||
			(anchor.target && anchor.target !== "_self")
		) {
			return;
		}

		const currentHref = anchor.getAttribute("href");
		if (!currentHref || currentHref.startsWith("#")) {
			return;
		}

		if (showMobileRouteSheet) {
			const preservedHref = preserveMobileRouteSheetHref({
				destinationHref: currentHref,
				state: mobileReturnState,
			});
			if (preservedHref !== currentHref) {
				anchor.setAttribute("href", preservedHref);
				return;
			}
		}

		const rewrittenHref = rewriteMobileCrossTabHref({
			currentPageID: pageID,
			destinationHref: currentHref,
			playerTid,
		});
		if (rewrittenHref !== currentHref) {
			anchor.setAttribute("href", rewrittenHref);
		}
	};

	// N1: keep the persistent navigation back stack in sync on every settled
	// navigation so the global TitleBar back control can climb ≥3 levels.
	useEffect(() => {
		if (!inLeague) {
			return;
		}
		syncMobileBackStack({
			hasPendingNegotiations: playMenuOptions.some(
				(option) => option.id === "contractNegotiationList",
			),
			needsExpansionProtection: playMenuOptions.some(
				(option) => option.id === "expansionDraft",
			),
			pageID,
			phase,
			playerTid,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [backStackUrl, inLeague, pageID, phase, playMenuOptions, playerTid]);

	const mobileCoreLoopV4Enabled = isMobileCoreLoopV4Enabled();
	const mobileIAV3Enabled = isMobileIAV3Enabled();
	const shellPolicy = getMobileAppShellPolicy({
		inLeague,
		mobileCoreLoopV4Enabled,
		mobileIAV3Enabled,
		pageID,
	});
	const { chromePolicy } = shellPolicy;
	const showMobileLeagueBottomChrome = shellPolicy.bodyBottomChrome;

	// Scroll to top if this load came from user clicking a link to a new page
	useEffect(() => {
		if (scrollToTop) {
			window.scrollTo(window.pageXOffset, 0);
		}
	}, [idLoaded, scrollToTop]);

	useEffect(() => {
		document.body.classList.toggle(
			"mobile-league-bottom-chrome",
			showMobileLeagueBottomChrome,
		);
		return () => {
			document.body.classList.remove("mobile-league-bottom-chrome");
		};
	}, [showMobileLeagueBottomChrome]);

	return (
		<LazyMotion strict features={loadFramerMotionFeatures}>
			{shellPolicy.showAppBar ? <MobileTopBar updating={updating} /> : null}
			<div
				className="h-100 d-flex"
				data-mobile-app-shell=""
				data-mobile-core-loop-enabled={
					mobileCoreLoopV4Enabled ? "true" : "false"
				}
				data-mobile-page-mode={shellPolicy.pageMode}
				data-mobile-shell-variant={shellPolicy.shellVariant}
			>
				<div className="h-100 w-100 d-flex flex-column" style={minWidth0}>
					{shellPolicy.showScoreStrip && !showMobileRouteSheet ? (
						<LeagueTopBar scoreMode={chromePolicy.scoreMode} />
					) : null}
					{shellPolicy.showContextHeader && !showMobileRouteSheet ? (
						<TitleBar chromeDensity={chromePolicy.titleDensity} />
					) : null}
					<div className="container-fluid position-relative mt-2 flex-grow-1 h-100">
						<div className="d-flex" style={minHeight100}>
							<div className="w-100 d-flex flex-column" style={minWidth0}>
								<main
									id="actual-actual-content"
									className="mobile-app-main clearfix"
									data-mobile-cross-tab-click-guard=""
									onClickCapture={handleContentClickCapture}
								>
									<ErrorBoundary key={idLoaded}>
										{showMobileRouteSheet && routeSheetReturnHref ? (
											<div
												aria-label={routeSheetTitle}
												aria-modal="true"
												className="mobile-cross-tab-route-sheet"
												data-mobile-cross-tab-route-sheet=""
												data-mobile-route-sheet-presentation={
													mobileReturnState.presentation
												}
												role="dialog"
											>
												<a
													aria-label={i18n.t("common.close", {
														ns: "ui",
														defaultValue: "Close",
													})}
													className="mobile-cross-tab-route-backdrop"
													data-mobile-cross-tab-route-close=""
													href={routeSheetReturnHref}
												/>
												<section className="mobile-cross-tab-route-panel">
													<header className="mobile-cross-tab-route-header">
														<a
															aria-label={i18n.t("common.back", {
																ns: "ui",
																defaultValue: "Back",
															})}
															className="mobile-cross-tab-route-back text-decoration-none"
															data-mobile-cross-tab-route-close=""
															href={routeSheetReturnHref}
														>
															<span aria-hidden="true">‹</span>
															<span>
																{i18n.t("common.back", {
																	ns: "ui",
																	defaultValue: "Back",
																})}
															</span>
														</a>
														<h2 className="h5 mb-0 mobile-cross-tab-route-title">
															{routeSheetTitle}
														</h2>
														<a
															aria-label={i18n.t("common.close", {
																ns: "ui",
																defaultValue: "Close",
															})}
															className="btn-close mobile-cross-tab-route-close"
															data-mobile-cross-tab-route-close=""
															href={routeSheetReturnHref}
														/>
													</header>
													<div className="mobile-cross-tab-route-body">
														{renderedView}
													</div>
												</section>
											</div>
										) : (
											renderedView
										)}
									</ErrorBoundary>
								</main>
							</div>
						</div>
					</div>
				</div>
			</div>
			{shellPolicy.showPrimaryCTA ? <MobilePrimaryCTA /> : null}
			{shellPolicy.showBottomNavigation ? <BottomTabBar /> : null}
			<Notifications />
			<MobileHighlightCard />
			<MobileMomentOverlay />
			<GlobalLoadingOverlay />
		</LazyMotion>
	);
};
